import React, { useState, useEffect } from 'react';

// Bumped when policy text changes. If a stored decision predates this version,
// the banner reappears so users can re-consent to the new policy.
const COOKIE_POLICY_VERSION = '1.0';
const STORAGE_KEY = 'cookie_consent_v1';

function readDecision() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.version !== COOKIE_POLICY_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeDecision(choice) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      version:   COOKIE_POLICY_VERSION,
      choice,                            // 'accept' | 'reject'
      decidedAt: new Date().toISOString(),
    }));
  } catch {
    // localStorage may be unavailable in some incognito modes — fail silently
  }
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!readDecision()) setVisible(true);
  }, []);

  if (!visible) return null;

  const decide = (choice) => {
    writeDecision(choice);
    setVisible(false);
    // Notify analytics scripts that may be gated on consent
    window.dispatchEvent(new CustomEvent('cookie-consent', { detail: { choice } }));
  };

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      style={{
        position: 'fixed',
        bottom: '1rem',
        left:   '1rem',
        right:  '1rem',
        maxWidth: '720px',
        margin: '0 auto',
        background: 'white',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        padding: '1rem 1.25rem',
        boxShadow: '0 8px 32px rgba(15, 23, 42, 0.12)',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
        <p style={{ margin: 0, fontSize: '0.95rem', fontWeight: 600, color: '#0f172a' }}>
          Cookies on this site
        </p>
        <p style={{ margin: 0, fontSize: '0.825rem', color: '#475569', lineHeight: 1.5 }}>
          We use essential cookies for authentication and security. With your consent, we also use
          cookies for product analytics to improve the platform. You can change your choice anytime
          in your account settings.
        </p>
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        <button
          onClick={() => decide('reject')}
          style={{
            padding: '0.5rem 1rem',
            fontSize: '0.825rem',
            fontWeight: 500,
            color: '#475569',
            background: 'white',
            border: '1px solid #cbd5e1',
            borderRadius: '8px',
            cursor: 'pointer',
          }}
        >
          Reject non-essential
        </button>
        <button
          onClick={() => decide('accept')}
          style={{
            padding: '0.5rem 1rem',
            fontSize: '0.825rem',
            fontWeight: 600,
            color: 'white',
            background: '#42a4bf',
            border: '1px solid #42a4bf',
            borderRadius: '8px',
            cursor: 'pointer',
          }}
        >
          Accept all
        </button>
      </div>
    </div>
  );
}

// Helper for analytics-gating — read by other modules to decide whether to
// load Mixpanel/PostHog/etc. Returns 'accept' | 'reject' | null.
export function getCookieConsent() {
  return readDecision()?.choice ?? null;
}
